import i18n from "@/i18n.ts";
import {
  ApiPerformanceReviewInvitationMode,
  ApiPerformanceReviewReviewerTypes,
} from "@/types/performance-review.types.ts";
import { ApiFile, ApiIdTitle } from "@/types/common.types.ts";
import { ApiNotificationChannelType } from "@/types/notification.types.ts";
import { Rule, RuleGroup, RuleGroupShort } from "@/types/rule.types.ts";
import { ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { ApiCalendar } from "@/types/calendar.types.ts";
import { ApiEventVisibility } from "@/types/event.types.ts";
import { ReminderMode, ReminderPeriod, ReminderSettingsPayload } from "@/types/reminder.types.ts";
import { EsignSignatureType } from "@/types/esign.types.ts";
import { ApiWorkflowConditionSave } from "@/types/workflows/workflow.condition.types.ts";
import { SelectItem } from "@skailer-com/uikit";
import {
  ApiWorkflowBlockDelayReference,
  ApiWorkflowEventTypes,
  ApiWorkflowMeetingDateReference,
  ApiWorkflowTriggerScheduleDayType,
  ApiWorkflowTriggerScheduleEntityField,
  ApiWorkflowTriggerScheduleEntityType,
  isCandidateWorkflowEventType,
} from "@/types/workflows/workflow.base-enum.types.ts";

export interface ApiWorkflowBlockTimingFields {
  delay_days: number;
  delay_hours: number;
  delay_minutes: number;
  delay_reference: ApiWorkflowBlockDelayReference;
  delay_day_type: ApiWorkflowTriggerScheduleDayType;
}

export enum ApiWorkflowBlockActionNotificationRecipientTypes {
  employee = "employee",
  employee_subordinates = "employee_subordinates",

  employee_manager = "employee_manager",
  employee_managers_subordinates = "employee_managers_subordinates",

  custom_field_employee_reference = "custom_field_employee_reference",
  custom_field_employee_reference_subordinates = "custom_field_employee_reference_subordinates",

  specific_employee = "specific_employee",

  candidate_job_recruiters = "candidate_job_recruiters",
  candidate_job_stakeholders = "candidate_job_stakeholders",

  external_emails = "external_emails",
  slack_channels = "slack_channels",
}

export interface ApiWorkflowBlockActionNotification {
  subject: string;
  body: string;
  recipient_type: ApiWorkflowBlockActionNotificationRecipientTypes;
  recipient_value: string[];
  to_channels: ApiNotificationChannelType[];
  skip_notification_to_affected_employee: boolean;

  recipient_entities: ApiIdTitle[];
}

export interface ApiWorkflowBlockActionNotificationSave extends ApiWorkflowBlockTimingFields {
  title: string | null;

  subject: string;
  body: string;
  recipient_type: ApiWorkflowBlockActionNotificationRecipientTypes;
  recipient_value: string[];
  to_channels: ApiNotificationChannelType[];
  skip_notification_to_affected_employee: boolean;
}

export interface ApiWorkflowBlockActionCandidateEmail {
  from_email: string | null;
  sender_id: number | null;
  subject: string;
  body: string;
  template_id: number | null;
  file_uuids: string[];
}

export interface ApiWorkflowBlockActionCandidateEmailSave extends ApiWorkflowBlockTimingFields {
  title: string | null;

  from_email: string;
  subject: string;
  body: string;
  template_id: number | null;
  file_uuids: string[];
}

export enum ApiWorkflowBlockActionCandidateStageChangeMode {
  change_stage = "change_stage",
  disqualify = "disqualify",
}

export interface ApiWorkflowBlockActionCandidateStageChange {
  mode: ApiWorkflowBlockActionCandidateStageChangeMode;
  stage_id: number | null;
  disqualification_reason_id: number | null;
  reserved: boolean;
  comment: string | null;
}

export interface ApiWorkflowBlockActionCandidateStageChangeSave {
  mode: ApiWorkflowBlockActionCandidateStageChangeMode;
  stage_id: number | null;
  disqualification_reason_id: number | null;
  reserved: boolean;
  comment: string | null;
}

export interface ApiWorkflowBlockActionHTTPRequest {
  http_method: string;
  url: string;
  custom_headers: Record<string, string>;
  payload_template: string;
  // Optional for compatibility with workflow blocks saved before HMAC signatures were introduced.
  has_signature_secret?: boolean;
}

export interface ApiWorkflowBlockActionHTTPRequestSignatureSecret {
  signature_secret: string;
}

export interface ApiWorkflowBlockActionHTTPRequestSave extends ApiWorkflowBlockTimingFields {
  title: string | null;

  http_method: string;
  url: string;
  custom_headers: Record<string, string>;
  payload_template: string;
}

export enum ApiWorkflowBlockActionGoogleWorkspaceType {
  create_user = "create_user",
  delete_user = "delete_user",
}

export interface ApiWorkflowBlockActionGoogleWorkspace {
  action_type: ApiWorkflowBlockActionGoogleWorkspaceType;
}

export interface ApiWorkflowBlockActionGoogleWorkspaceSave {
  action_type: ApiWorkflowBlockActionGoogleWorkspaceType;
}

export enum ApiInviteEmailType {
  work = "work",
  personal = "personal",
}

export interface ApiWorkflowBlockActionInvitation {
  subject: string;
  body: string;
  invite_email_type: ApiInviteEmailType;
}

export interface ApiWorkflowBlockActionInvitationSave {
  subject: string;
  body: string;
  invite_email_type: ApiInviteEmailType;
}

export interface ApiWorkflowBlockActionSurvey {
  title: string;
  description: string | null;
  emoji: string | null;
  use_ai: boolean;
  form_template_id: number;
  participants_rule_groups?: RuleGroup[] | null;
  participants_rules?: Rule[] | null;
  share_employee_rule_groups?: RuleGroup[] | null;
  share_employee_rules?: Rule[] | null;
  invitation_subject: string | null;
  invitation_body: string | null;
  timeframe_period_type: ApiWorkflowSurveyPeriodType;
  timeframe_period_value: number;
  is_anonymous: boolean;
  reminder_settings: ReminderSettingsPayload | null;
}

export interface ApiWorkflowBlockActionSurveySave {
  title: string;
  description: string | null;
  emoji: string | null;
  use_ai: boolean;
  form_template_id: number;
  participants_rule_groups: RuleGroupShort[];
  share_employee_rule_groups: RuleGroupShort[];
  invitation_subject: string | null;
  invitation_body: string | null;
  timeframe_period_type: ApiWorkflowSurveyPeriodType;
  timeframe_period_value: number;
  is_anonymous: boolean;
  reminder_settings: ReminderSettingsPayload | null;
}

export interface ApiWorkflowBlockActionPerformanceReviewReviewerType {
  id: number | null;
  reviewer_type?: ApiPerformanceReviewReviewerTypes | null;
  type?: ApiPerformanceReviewReviewerTypes | null;
  title: string | null;
  form_template_id: number | null;
  is_enabled?: boolean | null;
  is_anonymous?: boolean | null;
  anonymous_threshold?: number | null;
  participants_rule_groups?: RuleGroup[] | null;
  participants_rules?: Rule[] | null;
  invitation_subject: string | null;
  invitation_body: string | null;
}

export interface ApiWorkflowBlockActionPerformanceReviewReviewerTypeSave {
  id: number | null;
  title: string | null;
  type: ApiPerformanceReviewReviewerTypes;
  form_template_id: number;
  anonymous_threshold: number;
  participants_rule_groups: RuleGroupShort[];
  invitation_subject: string | null;
  invitation_body: string | null;
}

export interface ApiWorkflowBlockActionPerformanceReview {
  title: string;
  description: string | null;
  emoji: string | null;
  use_ai: boolean;
  participants_rule_groups?: RuleGroup[] | null;
  participants_rules?: Rule[] | null;
  invitation_subject: string | null;
  invitation_body: string | null;
  invitation_mode: ApiPerformanceReviewInvitationMode;
  timeframe_period_type: ApiWorkflowSurveyPeriodType;
  timeframe_period_value: number;
  reviewer_types: ApiWorkflowBlockActionPerformanceReviewReviewerType[];
  reminder_settings: ReminderSettingsPayload | null;
}

export interface ApiWorkflowBlockActionPerformanceReviewSave {
  title: string;
  description: string | null;
  emoji: string | null;
  use_ai: boolean;
  participants_rule_groups: RuleGroupShort[];
  invitation_subject: string | null;
  invitation_body: string | null;
  invitation_mode: ApiPerformanceReviewInvitationMode;
  timeframe_period_type: ApiWorkflowSurveyPeriodType;
  timeframe_period_value: number;
  reviewer_types: ApiWorkflowBlockActionPerformanceReviewReviewerTypeSave[];
  reminder_settings: ReminderSettingsPayload | null;
}

export interface ApiWorkflowBlockActionPerformanceReviewEnrollReviewerTypeSave {
  reviewer_type_id: number;
  participants_rule_groups: RuleGroupShort[];
}

export interface ApiWorkflowBlockActionPerformanceReviewEnrollReviewerType {
  reviewer_type_id: number;
  participants_rule_groups?: RuleGroup[] | null;
  participants_rules?: Rule[] | null;
}

export interface ApiWorkflowBlockActionPerformanceReviewEnroll {
  review_id: number;
  reviewer_types: ApiWorkflowBlockActionPerformanceReviewEnrollReviewerType[];
}

export interface ApiWorkflowBlockActionPerformanceReviewEnrollSave {
  review_id: number;
  reviewer_types: ApiWorkflowBlockActionPerformanceReviewEnrollReviewerTypeSave[];
}

export interface ApiWorkflowBlockActionSurveyEnroll {
  survey_id: number;
}

export interface ApiWorkflowBlockActionSurveyEnrollSave {
  survey_id: number;
}

export enum ApiWorkflowSurveyPeriodType {
  day = "day",
  week = "week",
  month = "month",
}

export interface ApiWorkflowBlockActionTask {
  title: string;
  description: string | null;
  assign_to_rule_groups?: RuleGroup[] | null;
  assign_to_rules?: Rule[] | null;
  watcher_rule_groups?: RuleGroup[] | null;
  watcher_rules?: Rule[] | null;
  use_due_date: boolean;
  due_date_delay_days: number | null;
  files: ApiFile[];
  reminder_settings: ReminderSettingsPayload | null;
}

export interface ApiWorkflowBlockActionTaskSave {
  title: string;
  description: string | null;
  assign_to_rule_groups: RuleGroupShort[];
  watcher_rule_groups: RuleGroupShort[];
  use_due_date: boolean;
  due_date_delay_days: number | null;
  file_uuids: string[];
  reminder_settings: ReminderSettingsPayload | null;
}

export interface ApiWorkflowMeetingDate {
  reference: ApiWorkflowMeetingDateReference;
  entity_type: ApiWorkflowTriggerScheduleEntityType | null;
  entity_field: ApiWorkflowTriggerScheduleEntityField | null;
  offset_days: number;
  day_type: ApiWorkflowTriggerScheduleDayType;
}

export interface ApiWorkflowBlockActionMeeting {
  organizer: ApiEmployeeShort | null;
  calendar: ApiCalendar | null;
  title: string;
  description: string | null;
  location: string | null;
  visibility: ApiEventVisibility;
  attendee_rule_groups: RuleGroup[];
  external_attendee_emails: string[];
  start_time: string;
  duration_minutes: number;
  is_conference: boolean;
  meeting_date: ApiWorkflowMeetingDate;
}

export interface ApiWorkflowBlockActionMeetingSave {
  organizer_id: number | null;
  calendar_id: number | null;
  title: string;
  description: string | null;
  location: string | null;
  visibility: ApiEventVisibility;
  attendee_rule_groups: RuleGroupShort[];
  external_attendee_emails: string[];
  start_time: string;
  duration_minutes: number;
  is_conference: boolean;
  meeting_date: ApiWorkflowMeetingDate | null;
}

export interface ApiWorkflowBlockActionRunWorkflow {
  target_workflow: { id: number; title: string } | null;
}

export interface ApiWorkflowBlockActionRunWorkflowSave {
  target_workflow_id: number | null;
}

export interface ApiWorkflowBlockActionApproval {
  category_id: number;
  survey_template_id: number;
  when_to_save_responses: WorkflowApprovalWriteResponsesType;
  public_title: string | null;
  public_description: string | null;
}

export interface ApiWorkflowBlockActionApprovalSave {
  category_id: number;
  survey_template_id: number;
  when_to_save_responses: WorkflowApprovalWriteResponsesType;
  public_title: string | null;
  public_description: string | null;
}

export interface ApiWorkflowBlockActionEsign {
  document_id: number | null;
  target_folder_id: number | null;
  signature_type: EsignSignatureType;
  use_due_date: boolean;
  due_date_delay_days: number | null;
  reminder_mode: ReminderMode | null;
  reminder_custom_frequency: number | null;
  reminder_custom_period: ReminderPeriod | null;
}

export type ApiWorkflowBlockActionEsignSave = ApiWorkflowBlockActionEsign;

export enum WorkflowApprovalWriteResponsesType {
  after_approval = "after_approval",
  after_nested_steps_completed = "after_nested_steps_completed",
  after_workflow_completed = "after_workflow_completed",
}

export const getEsignSignatureTypeTitle = (type: EsignSignatureType): string => {
  switch (type) {
    case EsignSignatureType.signature:
      return i18n.t("workflow:action_type_esign_summary_signature");
    case EsignSignatureType.acknowledge:
      return i18n.t("workflow:action_type_esign_summary_acknowledgment");
  }
};

export const getWorkflowApprovalWriteResponsesTypeTitle = (
  type: WorkflowApprovalWriteResponsesType
): string => {
  switch (type) {
    case WorkflowApprovalWriteResponsesType.after_approval:
      return i18n.t("workflow:approval_save_responses_after_approval");
    case WorkflowApprovalWriteResponsesType.after_nested_steps_completed:
      return i18n.t("workflow:approval_save_responses_after_nested_steps");
    case WorkflowApprovalWriteResponsesType.after_workflow_completed:
      return i18n.t("workflow:approval_save_responses_after_workflow");
  }
};

export const getWorkflowApprovalWriteResponsesTypeItems = (): SelectItem[] => {
  return Object.values(WorkflowApprovalWriteResponsesType).map((type) => ({
    id: type,
    title: getWorkflowApprovalWriteResponsesTypeTitle(type),
  }));
};

export interface ApiWorkflowBlockActionSavePayload<T> {
  title: string | null;
  delay_days: number;
  delay_hours: number;
  delay_minutes: number;
  delay_reference: ApiWorkflowBlockDelayReference;
  delay_day_type: ApiWorkflowTriggerScheduleDayType;

  conditions: ApiWorkflowConditionSave[];
  block_data: T;
}

export const getApiWorkflowBlockActionNotificationRecipientTypesTitle = (
  type: ApiWorkflowBlockActionNotificationRecipientTypes
): string => {
  switch (type) {
    case ApiWorkflowBlockActionNotificationRecipientTypes.employee:
      return i18n.t("workflow:action_type_notification_recipient_employee");
    case ApiWorkflowBlockActionNotificationRecipientTypes.employee_subordinates:
      return i18n.t("workflow:action_type_notification_recipient_employee_subordinates");
    case ApiWorkflowBlockActionNotificationRecipientTypes.employee_manager:
      return i18n.t("workflow:action_type_notification_recipient_employee_manager");
    case ApiWorkflowBlockActionNotificationRecipientTypes.employee_managers_subordinates:
      return i18n.t("workflow:action_type_notification_recipient_employee_managers_subordinates");
    case ApiWorkflowBlockActionNotificationRecipientTypes.custom_field_employee_reference:
      return i18n.t("workflow:action_type_notification_recipient_custom_field_employee_reference");
    case ApiWorkflowBlockActionNotificationRecipientTypes.custom_field_employee_reference_subordinates:
      return i18n.t(
        "workflow:action_type_notification_recipient_custom_field_employee_reference_subordinates"
      );
    case ApiWorkflowBlockActionNotificationRecipientTypes.specific_employee:
      return i18n.t("workflow:action_type_notification_recipient_specific_employee");
    case ApiWorkflowBlockActionNotificationRecipientTypes.candidate_job_recruiters:
      return i18n.t("workflow:action_type_notification_recipient_candidate_job_recruiters");
    case ApiWorkflowBlockActionNotificationRecipientTypes.candidate_job_stakeholders:
      return i18n.t("workflow:action_type_notification_recipient_candidate_job_stakeholders");
    case ApiWorkflowBlockActionNotificationRecipientTypes.external_emails:
      return i18n.t("workflow:action_type_notification_recipient_external_emails");
    case ApiWorkflowBlockActionNotificationRecipientTypes.slack_channels:
      return i18n.t("workflow:action_type_notification_recipient_slack_channels");
  }
};

const affectedEmployeeNotificationRecipientTypes = [
  ApiWorkflowBlockActionNotificationRecipientTypes.employee,
  ApiWorkflowBlockActionNotificationRecipientTypes.employee_subordinates,
  ApiWorkflowBlockActionNotificationRecipientTypes.employee_manager,
  ApiWorkflowBlockActionNotificationRecipientTypes.employee_managers_subordinates,
  ApiWorkflowBlockActionNotificationRecipientTypes.custom_field_employee_reference,
  ApiWorkflowBlockActionNotificationRecipientTypes.custom_field_employee_reference_subordinates,
];

const candidateJobNotificationRecipientTypes = [
  ApiWorkflowBlockActionNotificationRecipientTypes.candidate_job_recruiters,
  ApiWorkflowBlockActionNotificationRecipientTypes.candidate_job_stakeholders,
];

export const getApiWorkflowBlockActionNotificationRecipientTypesSelectItems = (
  eventType?: ApiWorkflowEventTypes | null
): SelectItem[] => {
  const hiddenTypes = isCandidateWorkflowEventType(eventType)
    ? affectedEmployeeNotificationRecipientTypes
    : candidateJobNotificationRecipientTypes;
  return Object.values(ApiWorkflowBlockActionNotificationRecipientTypes)
    .filter((type) => !hiddenTypes.includes(type))
    .map((type) => ({
      id: type,
      title: getApiWorkflowBlockActionNotificationRecipientTypesTitle(type),
    }));
};

export const getApiInviteEmailTypeTitle = (type: ApiInviteEmailType): string => {
  switch (type) {
    case ApiInviteEmailType.work:
      return i18n.t("core:work_email");
    case ApiInviteEmailType.personal:
      return i18n.t("core:email_personal");
  }
};

export const getApiInviteEmailTypeSelectItems = (): SelectItem[] => {
  return Object.values(ApiInviteEmailType).map((type) => ({
    id: type,
    title: getApiInviteEmailTypeTitle(type),
  }));
};

export const getWorkflowSurveyPeriodTypeTitle = (type: ApiWorkflowSurveyPeriodType): string => {
  switch (type) {
    case ApiWorkflowSurveyPeriodType.day:
      return i18n.t("common:days");
    case ApiWorkflowSurveyPeriodType.week:
      return i18n.t("common:weeks");
    case ApiWorkflowSurveyPeriodType.month:
      return i18n.t("common:months");
  }
};

export const getWorkflowSurveyPeriodItems = (): SelectItem[] => {
  return Object.values(ApiWorkflowSurveyPeriodType).map((type) => ({
    id: type,
    title: getWorkflowSurveyPeriodTypeTitle(type),
  }));
};
